import { apiGet, apiPut } from "@/integrations/api/client";
import type { PresentationLocale } from "../slides/types";

type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

export type DownloadMetricKind =
  | "full_pdf"
  | "experience_pdf"
  | "presentation_pdf"
  | "presentation_open";

const METRICS_KEY = "engineering_download_metrics";

export type DownloadMetricsState = {
  full_pdf: number;
  experience_pdf: number;
  presentation_pdf: number;
  presentation_open: number;
  total: number;
  updated_at: string;
  recent: Array<{
    kind: DownloadMetricKind;
    locale: PresentationLocale;
    ts: string;
  }>;
};

const DEFAULT_METRICS: DownloadMetricsState = {
  full_pdf: 0,
  experience_pdf: 0,
  presentation_pdf: 0,
  presentation_open: 0,
  total: 0,
  updated_at: new Date(0).toISOString(),
  recent: [],
};

function parseMetrics(value: Json | null | undefined): DownloadMetricsState {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return DEFAULT_METRICS;
  }

  const input = value as Record<string, unknown>;
  const safeRecent = Array.isArray(input.recent)
    ? input.recent
        .map((item) => {
          if (!item || typeof item !== "object" || Array.isArray(item)) {
            return null;
          }
          const event = item as Record<string, unknown>;
          if (
            typeof event.kind !== "string" ||
            typeof event.locale !== "string" ||
            typeof event.ts !== "string"
          ) {
            return null;
          }
          if (!["full_pdf", "experience_pdf", "presentation_pdf", "presentation_open"].includes(event.kind)) {
            return null;
          }
          if (!["it", "en"].includes(event.locale)) {
            return null;
          }
          return {
            kind: event.kind as DownloadMetricKind,
            locale: event.locale as PresentationLocale,
            ts: event.ts,
          };
        })
        .filter(Boolean)
        .slice(0, 25) as DownloadMetricsState["recent"]
    : [];

  return {
    full_pdf: Number(input.full_pdf ?? 0),
    experience_pdf: Number(input.experience_pdf ?? 0),
    presentation_pdf: Number(input.presentation_pdf ?? 0),
    presentation_open: Number(input.presentation_open ?? 0),
    total: Number(input.total ?? 0),
    updated_at: typeof input.updated_at === "string" ? input.updated_at : DEFAULT_METRICS.updated_at,
    recent: safeRecent,
  };
}

async function fetchRow() {
  try {
    const data = await apiGet<{ value: Json }>(`/settings/${METRICS_KEY}`);
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
}

export async function getDownloadMetrics(): Promise<DownloadMetricsState | null> {
  const { data, error } = await fetchRow();
  if (error) return null;
  return parseMetrics(data?.value);
}

export async function recordDownloadMetric(
  kind: DownloadMetricKind,
  locale: PresentationLocale
): Promise<DownloadMetricsState | null> {
  const { data, error } = await fetchRow();
  if (error) return null;

  const current = parseMetrics(data?.value);
  const now = new Date().toISOString();

  const next: DownloadMetricsState = {
    ...current,
    [kind]: (current[kind] ?? 0) + 1,
    total: current.total + 1,
    updated_at: now,
    recent: [{ kind, locale, ts: now }, ...current.recent].slice(0, 25),
  };

  try {
    await apiPut("/admin/settings", {
      key: METRICS_KEY,
      value: next,
    });
    return next;
  } catch {
    return null;
  }
}
